// src/react-auth0-spa.js
import React, { useState, useEffect, useContext } from "react";
import { UserManager } from "oidc-client";
import { OidcSettings } from "./oidcsettings";
import NotificationService from "./api/NotificationService";
import CompanyService from "./api/CompanyService";
import CampaignService from "./api/CampaignService";
import PlatformUserService from "./api/PlatformUserService";
import ConfigurationService from "./api/ConfigurationService";
import IndicatorService from "./api/IndicatorService";
import LoanService from "./api/LoanService";
import TestService from "./api/TestService";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

export const AuthContext = React.createContext();
export const useAuth = () => useContext(AuthContext);
export const AuthProvider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingUser, setLoadingUser] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [errors, setErrors] = useState({});
  const [notificationService, setNotificationService] = useState();
  const [companyService, setCompanyService] = useState();
  const [campaignService, setCampaignService] = useState();
  const [platformUserService, setPlatformUserService] = useState();
  const [configurationService, setConfigurationService] = useState();
  const [indicatorService, setIndicatorService] = useState();
  const [loanService, setLoanService] = useState();
  const [testService, setTestService] = useState();
  const userManager = new UserManager(OidcSettings);
  const [userContext, setUserContext] = useState({});

  useEffect(() => {
    
    setCompanyService(new CompanyService());

    const initAuth = async () => {
      userManager.events.addUserLoaded(onUserLoaded);
      userManager.events.addUserUnloaded(onUserUnloaded);
      userManager.events.addUserSignedOut(onUserLogout);
      userManager.events.addAccessTokenExpiring(onUserUnloaded);

      userManager.getUser().then(user => {
        if (user !== null && user !== undefined) {
          if (user.expired) return;
          onUserLoaded(user);
        } else if (window.location.href.includes("#id_token")) {
          setLoadingUser(true);
          return userManager
            .signinRedirectCallback()
            .then(() => {
              onRedirectCallback();
            })
            .catch(function(err) {
              // eslint-disable-next-line no-console
              console.log("Error signinRedirectCallback: ", err);
            });
        } else {
          setLoading(false);
        }
      });
    };

    initAuth();
    // eslint-disable-next-line
    }, []);

  const onUserLoaded = async user => {
    setIsAuthenticated(true);
    setUser(user);
    setNotificationService(
      new NotificationService(user.access_token, user.profile.name)
    );
    setCompanyService(new CompanyService(user.access_token));
    setCampaignService(new CampaignService(user.access_token));
    setPlatformUserService(new PlatformUserService(user.access_token));
    setConfigurationService(new ConfigurationService(user.access_token));
    setIndicatorService(new IndicatorService(user.access_token));
    setLoanService(new LoanService(user.access_token));
    setTestService(new TestService(user.access_token));
    // search user permissions
    searchContext(user.access_token);
    setLoadingUser(false);
  };

  const searchContext = async token => {
    setLoading(true);
    const apiService = new PlatformUserService(token);
    const response = await apiService.getAuthContext();
    if (response && !response.result) {
      // user not logget correct
      // call logout
      setIsNewUser({});
    } else if (response && response.object && response.object.invitation) {
      // eslint-disable-next-line no-console
      setIsNewUser({ invitation: response.object.invitation });
    } else {
      setIsNewUser(false);
      setUserContext(response.object);
    }
    setLoading(false);
  };

  const onUserUnloaded = () => {
    setIsAuthenticated(false);
    setUser(undefined);
    localStorage.clear();
    sessionStorage.clear();
  };

  const signin = () => {
    setErrors({});

    userManager
      .signinRedirect()
      .then(function() {
        // eslint-disable-next-line no-console
        console.log("signinRedirect ok");
      })
      .catch(function(err) {
        // eslint-disable-next-line no-console
        setErrors({
          server:
            "No se puede establecer conexion en estos momentos, intente mas tarde."
        });
      });
  };

  const logout = () => {
    setLoading(true);

    userManager
      .signoutRedirect()
      .then(function() {
        // eslint-disable-next-line no-console
        console.log("signoutRedirect ok");
      })
      .catch(function(err) {
        // eslint-disable-next-line no-console
        console.log("signoutRedirect error:", err);
      });
  };

  const onUserLogout = () => {
    userManager
      .signoutRedirectCallback()
      .then(() => {
        userManager.removeUser();
      })
      .then(() => {
        setUser(null);
      })
      // eslint-disable-next-line no-console
      .catch(error => console.log(error));
  };

  return (
    <AuthContext.Provider
      value={{
        campaignService,
        companyService,
        configurationService,
        errors,
        indicatorService,
        isAuthenticated,
        isNewUser,
        loading,
        loadingUser,
        loanService,
        logout: () => logout(),
        notificationService,
        platformUserService,
        signin: () => signin(),
        testService,
        user,
        userContext
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
